// What the AI makes of something PK captured (AI golf A, B31): one shape for the server, the fake and the app.

export type UnderstoodKind = "appointment" | "task" | "reminder" | "date";

export interface Understood {
  kind: UnderstoodKind;
  /** The AI's own word on it: below the bar it goes to "Te controleren" (AI3). */
  confidence: number;
  title: string;
  /** YYYY-MM-DD, or null. */
  date: string | null;
  /** HH:MM, or null. */
  time: string | null;
  durationMinutes: number | null;
  location: string | null;
  /** The name of one of PK's categories, or null. */
  category: string | null;
  estimateMinutes: 15 | 30 | 60 | null;
  /** In Dutch, short: why the AI is not sure (shown in Te controleren). */
  doubt: string | null;
  /** A yearly date (golf 9) that is someone's birthday; the title is then just the name. */
  birthday?: boolean | null;
  /** A task that is an idea or for some day (golf 10, B52): it goes to Ooit, not to Te doen. */
  someday?: boolean | null;
}

/** Above this the AI puts it in place itself; we start strict (AI3). */
export const SURE = 0.85;

/** Is a proposal complete enough to put in place? An appointment and a reminder need a day and a time. */
export function applicable(u: Understood): boolean {
  if (u.kind === "appointment" || u.kind === "reminder") return !!u.date && !!u.time;
  if (u.kind === "date") return !!u.date;
  return true;
}

export const UNDERSTOOD_SCHEMA = {
  type: "object",
  properties: {
    kind: { type: "string", enum: ["appointment", "task", "reminder", "date"] },
    confidence: { type: "number", minimum: 0, maximum: 1 },
    title: { type: "string" },
    date: { type: ["string", "null"], description: "YYYY-MM-DD" },
    time: { type: ["string", "null"], description: "HH:MM, 24 hour clock" },
    durationMinutes: { type: ["integer", "null"] },
    location: { type: ["string", "null"] },
    category: { type: ["string", "null"] },
    estimateMinutes: { type: ["integer", "null"], enum: [15, 30, 60, null] },
    doubt: { type: ["string", "null"] },
    birthday: { type: ["boolean", "null"], description: "Only for kind date: true when it is a birthday" },
    someday: { type: ["boolean", "null"], description: "Only for kind task: true when it is an idea or for some day" },
  },
  required: ["kind", "confidence", "title", "date", "time", "durationMinutes", "location", "category", "estimateMinutes", "doubt"],
} as const;

/** "Afspraak: Tandarts, dinsdag 21 oktober 10:00", the way a proposal reads in the app. */
export function describeUnderstood(u: Understood, displayDate: (d: string) => string): string {
  const when = [u.date ? displayDate(u.date) : null, u.time ?? null].filter(Boolean).join(" ");
  if (u.kind === "appointment") return `Afspraak: ${u.title}${when ? `, ${when}` : ""}`;
  if (u.kind === "reminder") return `Herinnering: ${u.title}${when ? `, ${when}` : ""}`;
  if (u.kind === "date") return `${u.birthday ? "Verjaardag" : "Elk jaar"}: ${u.title}${u.date ? `, ${displayDate(u.date)}` : ""}`;
  if (u.someday && !u.date) return `Ooit: ${u.title}`;
  return `Taak: ${u.title}${u.category ? ` in ${u.category}` : ""}${u.date ? `, ${displayDate(u.date)}` : ""}`;
}

const KINDS = new Set(["appointment", "task", "reminder", "date"]);

function validDate(value: unknown): string | null {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value ? value : null;
}

function text(value: unknown, max: number): string | null {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

/**
 * What the AI answered, checked before it is used (review: never trust the answer). Null when there is no usable
 * title; a wrong date, time or number becomes null, so it ends in Te controleren rather than in an error.
 */
export function cleanUnderstood(raw: unknown): Understood | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  const title = text(r.title, 300);
  if (!title) return null;
  const time = typeof r.time === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(r.time) ? r.time : null;
  const minutes =
    typeof r.durationMinutes === "number" && Number.isInteger(r.durationMinutes) && r.durationMinutes >= 5 && r.durationMinutes <= 1440
      ? r.durationMinutes
      : null;
  const estimate = r.estimateMinutes === 15 || r.estimateMinutes === 30 || r.estimateMinutes === 60 ? r.estimateMinutes : null;
  const confidence = typeof r.confidence === "number" && r.confidence >= 0 && r.confidence <= 1 ? r.confidence : 0;
  return {
    kind: KINDS.has(r.kind as string) ? (r.kind as UnderstoodKind) : "task",
    confidence,
    title,
    date: validDate(r.date),
    time,
    durationMinutes: minutes,
    location: text(r.location, 300),
    category: text(r.category, 60),
    estimateMinutes: estimate,
    doubt: text(r.doubt, 300),
    birthday: r.birthday === true,
    someday: r.someday === true,
  };
}
